import { useMemo, useState, type ReactNode } from 'react';
import { useInRouterContext, useNavigate } from 'react-router';
import { toast } from 'sonner';
import {
  AlertCircle,
  AlertOctagon,
  Bell,
  Building2,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Clock,
  Hammer,
  Inbox,
  Layers,
  MessageSquare,
  RefreshCw,
  User,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ConfigItemStatusControl } from './ConfigItemStatusControl';
import { useActionItemsUnified } from '../hooks/useActionItemsUnified';
import { projectId } from '../utils/supabase/info';
import { setImplementationStatus } from '../utils/discoveryGridApi';
import { actionCounts, groupActionRows, type ActionBreakdown, type ActionCounts, type ActionReason, type ActionRow } from '../utils/consultantActionItems';
import { URGENCY_LABELS, type Urgency } from '../utils/clientHome';
import { labelFor, type LifecycleStatus } from '../utils/configItemLifecycle';
import { formatDate } from '../utils/formatDate';
import type { Notification } from '../types';

/**
 * DashboardActionItems — the Queue's action items as one list (RESTORE_PLAN R2.4; Trevan's F5 note:
 * "a list, not cards… take all actions from the row… click in to see the whole message").
 *
 * Tasks (config items that need the consultant, or that are assigned with a due date and tracked)
 * are grouped by client, then by space, most urgent first; every action is on the row (Review,
 * the lifecycle move, Open) and a task row is not clickable. Notifications (the company view)
 * open in place to show the whole message. The counts are the old overview's four: total,
 * overdue, due today, due tomorrow.
 */

interface PreloadedActionItems {
  items: Array<ActionRow | Notification>;
  totalCount?: number;
  loading?: boolean;
  isLoading?: boolean;
  refresh: () => Promise<void> | void;
  tracking?: ActionRow[];
  breakdownCounts?: Partial<ActionBreakdown>;
  debugInfo?: { source?: string; count?: number };
}

interface DashboardActionItemsProps {
  accessToken: string;
  currentUser: any;
  companyId?: string;
  /** The dashboard's own hook result, so the list and the Queue's count never disagree. */
  preloadedData?: PreloadedActionItems;
  /** Opens the review panel for a row whose answers wait for review (the Queue owns the panel). */
  onReview?: (row: ActionRow) => void;
}

type View = 'needs' | 'tracking';

const isTask = (item: ActionRow | Notification): item is ActionRow => (item as ActionRow).kind === 'task';

export function DashboardActionItems({ accessToken, currentUser, companyId, preloadedData, onReview }: DashboardActionItemsProps) {
  // With preloaded data the internal hook stays idle (no token, no fetch).
  const internal = useActionItemsUnified({ accessToken: preloadedData ? '' : accessToken, currentUser, companyId });
  const data: PreloadedActionItems = preloadedData ?? internal;
  const loading = Boolean(data.isLoading ?? data.loading);
  const all = data.items ?? [];
  const tasks = useMemo(() => all.filter(isTask), [all]);
  const messages = useMemo(() => all.filter((i): i is Notification => !isTask(i)), [all]);
  const tracking = data.tracking ?? [];
  const breakdown = data.breakdownCounts;

  const [view, setView] = useState<View>('needs');
  const rows = view === 'needs' ? tasks : tracking;
  const groups = useMemo(() => groupActionRows(rows), [rows]);
  // Notifications count toward the total; only tasks carry due dates.
  const counts: ActionCounts = view === 'needs' ? { ...actionCounts(tasks), total: tasks.length + messages.length } : actionCounts(tracking);
  const hasTaskViews = !companyId;

  const refresh = () => { void data.refresh(); };

  const move = async (row: ActionRow, to: LifecycleStatus, comment?: string) => {
    try {
      await setImplementationStatus(row.companyId, row.itemId, { status: to, comment }, accessToken);
      toast.success(`${row.title}: ${labelFor(to).toLowerCase()}`);
      refresh();
    } catch (e: any) {
      toast.error(e?.message || 'Could not change the status');
    }
  };

  const markRead = async (notificationId: string) => {
    try {
      await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      refresh();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  return (
    <Card data-testid="action-items">
      <CardHeader className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>Action items</CardTitle>
            <CardDescription>
              {companyId ? 'Notifications and work for this client' : 'What needs you across your clients, by client and space'}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={refresh} title="Reload action items" aria-label="Reload action items">
            <RefreshCw className="size-4" aria-hidden="true" />
          </Button>
        </div>

        <CountStrip counts={counts} />

        {hasTaskViews && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div role="tablist" aria-label="Action item views" className="inline-flex rounded-md border p-0.5">
              <ViewTab active={view === 'needs'} onClick={() => setView('needs')} label="Needs you" count={tasks.length} />
              <ViewTab active={view === 'tracking'} onClick={() => setView('tracking')} label="Tracking" count={tracking.length} />
            </div>
            {view === 'needs' && breakdown && (
              <div className="flex flex-wrap gap-2" data-testid="action-breakdown">
                <Badge variant="outline">Assigned to me: {breakdown.assigned_to_me ?? 0}</Badge>
                <Badge variant="outline">Answers to review: {breakdown.waiting_review ?? 0}</Badge>
                {(breakdown.blocked ?? 0) > 0 && <Badge variant="destructive">Blocked: {breakdown.blocked}</Badge>}
              </div>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-5">
        {loading ? (
          <p className="py-8 text-center text-muted-foreground">Loading action items…</p>
        ) : (
          <>
            {groups.length === 0 && messages.length === 0 && (
              <div className="py-8 text-center" data-testid="action-items-empty">
                <Inbox className="mx-auto mb-3 size-10 text-muted-foreground" aria-hidden="true" />
                <p className="text-muted-foreground">
                  {view === 'tracking' ? 'Nothing is assigned with a due date and waiting on someone else.' : 'Nothing needs you right now.'}
                </p>
              </div>
            )}

            {groups.map((company) => (
              <CompanyGroup key={company.companyId} name={company.companyName} counts={company.counts} showName={!companyId}>
                {company.spaces.map((space) => (
                  <div key={space.spaceId ?? 'company-wide'} className="space-y-1.5">
                    <div className="flex items-center gap-2 px-1 text-muted-foreground">
                      <Layers className="size-3.5" aria-hidden="true" />
                      <span>{space.spaceName}</span>
                      <span aria-label={`${space.rows.length} items`}>· {space.rows.length}</span>
                    </div>
                    <ul role="list" className="divide-y rounded-md border bg-white">
                      {space.rows.map((row) => (
                        <TaskRow key={row.id} row={row} onReview={onReview} onMove={(to, comment) => move(row, to, comment)} />
                      ))}
                    </ul>
                  </div>
                ))}
              </CompanyGroup>
            ))}

            {view === 'needs' && messages.length > 0 && (
              <ul role="list" className="divide-y rounded-md border bg-white" aria-label="Notifications">
                {messages.map((n) => (
                  <MessageRow key={n.id} notification={n} showCompany={!companyId} onDone={() => markRead(n.id)} />
                ))}
              </ul>
            )}
          </>
        )}

        {typeof window !== 'undefined' && window.location.search.includes('debug=1') && (
          <div className="flex gap-4 border-t pt-2 font-mono text-muted-foreground opacity-50 hover:opacity-100">
            <span>ActionItemsCount: {data.debugInfo?.count ?? all.length}</span>
            <span>Tracking: {tracking.length}</span>
            <span>QuerySource: {data.debugInfo?.source ?? ''}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CountStrip({ counts }: { counts: ActionCounts }) {
  const cells: Array<{ label: string; value: number; tone: string }> = [
    { label: 'Total', value: counts.total, tone: '' },
    { label: 'Overdue', value: counts.overdue, tone: counts.overdue > 0 ? 'text-red-700' : '' },
    { label: 'Due today', value: counts.dueToday, tone: counts.dueToday > 0 ? 'text-orange-700' : '' },
    { label: 'Due tomorrow', value: counts.dueTomorrow, tone: counts.dueTomorrow > 0 ? 'text-amber-700' : '' },
  ];
  return (
    <dl className="grid grid-cols-2 divide-x divide-y rounded-md border md:grid-cols-4 md:divide-y-0" data-testid="action-counts">
      {cells.map((c) => (
        <div key={c.label} className="px-3 py-2">
          <dt className="text-muted-foreground">{c.label}</dt>
          <dd className={c.tone} data-testid={`action-count-${c.label.toLowerCase().replace(' ', '-')}`}>{c.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ViewTab({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count: number }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`rounded px-3 py-1 transition-colors ${active ? 'bg-slate-900 text-white' : 'text-muted-foreground hover:bg-muted'}`}
    >
      {label} <span className={active ? 'opacity-80' : ''}>{count}</span>
    </button>
  );
}

function CompanyGroup({ name, counts, showName, children }: { name: string; counts: ActionCounts; showName: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(true);
  if (!showName) return <section className="space-y-3">{children}</section>;
  return (
    <section className="space-y-3" aria-label={name}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex w-full items-center gap-2 border-b pb-1.5 text-left">
        {open ? <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" /> : <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />}
        <Building2 className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="min-w-0 truncate">{name}</span>
        <span className="shrink-0 whitespace-nowrap text-muted-foreground">· {counts.total} {counts.total === 1 ? 'item' : 'items'}</span>
        {counts.overdue > 0 && <Badge variant="destructive" className="ml-1 shrink-0">{counts.overdue} overdue</Badge>}
      </button>
      {open && children}
    </section>
  );
}

const URGENCY_TONES: Record<Urgency, string> = {
  overdue: 'bg-red-100 text-red-800 border-red-200',
  today: 'bg-orange-100 text-orange-800 border-orange-200',
  tomorrow: 'bg-amber-100 text-amber-800 border-amber-200',
  thisWeek: 'bg-slate-100 text-slate-700 border-slate-200',
  none: '',
};

const REASON_ICONS: Record<ActionReason, ReactNode> = {
  review_answers: <ClipboardCheck className="size-4 text-purple-600" aria-hidden="true" />,
  build: <Hammer className="size-4 text-green-700" aria-hidden="true" />,
  blocked: <AlertOctagon className="size-4 text-red-600" aria-hidden="true" />,
  overdue_with_client: <AlertCircle className="size-4 text-red-600" aria-hidden="true" />,
  client_court: <User className="size-4 text-blue-600" aria-hidden="true" />,
  tracking: <Clock className="size-4 text-slate-500" aria-hidden="true" />,
};

/** "Nov 3" for a stored due day; a bare YYYY-MM-DD is that local day, not UTC midnight. */
function dueDay(value: string | null): string {
  if (!value) return '';
  const local = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value;
  return formatDate(local, { month: 'short', day: 'numeric' });
}

function TaskRow({ row, onReview, onMove }: { row: ActionRow; onReview?: (row: ActionRow) => void; onMove: (to: LifecycleStatus, comment?: string) => void | Promise<void> }) {
  const due = dueDay(row.dueDate);
  const meta = [row.message, row.assignedToName ? `Assigned to ${row.assignedToName}` : '', due ? `Due ${due}` : ''].filter(Boolean);
  return (
    <li data-testid="action-row" data-row-id={row.id} className="flex flex-col gap-2 px-3 py-2.5 transition-colors hover:bg-slate-50 md:flex-row md:items-center">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <span className="mt-0.5 shrink-0">{REASON_ICONS[row.reason]}</span>
        <div className="min-w-0 space-y-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate">{row.title}</span>
            {row.urgency !== 'none' && (
              <span data-testid="urgency-badge" className={`rounded-full border px-2 py-0.5 ${URGENCY_TONES[row.urgency]}`}>{URGENCY_LABELS[row.urgency]}</span>
            )}
          </div>
          <p className="truncate text-muted-foreground">{meta.join(' · ')}</p>
          {row.assignmentNote && <p className="truncate text-muted-foreground" data-testid="assignment-note">Note: {row.assignmentNote}</p>}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2 md:justify-end">
        {row.reason === 'review_answers' && onReview && (
          <Button size="sm" onClick={() => onReview(row)}>Review</Button>
        )}
        <ConfigItemStatusControl item={{ status: row.status, blockedFrom: row.blockedFrom, name: row.title }} firm onMove={onMove} validationErrors={row.readinessErrors ?? []} />
        <OpenLink href={row.link} label={`Open ${row.title}`} />
      </div>
    </li>
  );
}

/** An in-app link when a router is mounted (the Queue is); a plain anchor otherwise (the system admin's company view). */
function OpenLink({ href, label }: { href: string; label: string }) {
  const inRouter = useInRouterContext();
  return inRouter ? <RouterOpenLink href={href} label={label} /> : (
    <Button variant="ghost" size="sm" asChild><a href={href} aria-label={label}>Open</a></Button>
  );
}

function RouterOpenLink({ href, label }: { href: string; label: string }) {
  const navigate = useNavigate();
  return (
    <Button variant="ghost" size="sm" asChild>
      <a href={href} aria-label={label} onClick={(e) => { e.preventDefault(); navigate(href); }}>Open</a>
    </Button>
  );
}

const MESSAGE_ICONS: Record<string, ReactNode> = {
  ready_for_implementation: <CheckCircle className="size-4 text-green-600" aria-hidden="true" />,
  ready_for_build: <CheckCircle className="size-4 text-green-600" aria-hidden="true" />,
  ready_for_review: <CheckCircle className="size-4 text-purple-600" aria-hidden="true" />,
  message: <MessageSquare className="size-4 text-blue-600" aria-hidden="true" />,
  homework_assigned: <ClipboardCheck className="size-4 text-orange-600" aria-hidden="true" />,
  status_change: <AlertCircle className="size-4 text-orange-600" aria-hidden="true" />,
};

function timeAgo(value: string): string {
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  if (mins < 10080) return `${Math.floor(mins / 1440)}d ago`;
  return formatDate(value);
}

/** A notification: the row opens in place to show the whole message; Done marks it read. */
function MessageRow({ notification: n, showCompany, onDone }: { notification: Notification; showCompany: boolean; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const companyName = (n as any).companyName as string | undefined;
  return (
    <li data-testid="message-row" className="flex items-start gap-3 px-3 py-2.5 hover:bg-slate-50">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex min-w-0 flex-1 items-start gap-3 text-left">
        <span className="mt-0.5 shrink-0">{MESSAGE_ICONS[n.type] ?? <Bell className="size-4" aria-hidden="true" />}</span>
        <span className="min-w-0 space-y-0.5">
          <span className="flex flex-wrap items-center gap-2">
            <span className="truncate">{n.title}</span>
            {showCompany && companyName && <Badge variant="outline">{companyName}</Badge>}
          </span>
          <span className={`block text-muted-foreground ${open ? 'whitespace-pre-wrap' : 'truncate'}`} data-testid="message-text">{n.message}</span>
          {open && n.configItemName && <span className="block text-muted-foreground">{n.configItemType}: {n.configItemName}</span>}
          {open && n.statusComment && <span className="block border-l-2 border-primary bg-muted px-2 py-1">Note: {n.statusComment}</span>}
          <span className="flex items-center gap-1 text-muted-foreground"><Clock className="size-3" aria-hidden="true" />{timeAgo(n.createdAt)}</span>
        </span>
      </button>
      <Button variant="ghost" size="sm" onClick={onDone}>
        <CheckCircle className="mr-2 size-4" aria-hidden="true" />
        Done
      </Button>
    </li>
  );
}
